import { useEffect, useMemo, useState } from 'react';
import { SerialService } from '../../services/serial.service';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

/**
 * F6b: which of `itemIds` are serial-tracked. `isTracked(id)` is true/false once known, or undefined when the caller
 * cannot ask (no STOCK_VIEW) or the lookup failed: dialogs then show an optional serial field and let the server decide.
 */
export function useSerialTrackedItems(itemIds: (string | null | undefined)[]) {
  const canView = usePermission(PERMISSIONS.STOCK_VIEW);
  const key = useMemo(
    () => Array.from(new Set(itemIds.filter((x): x is string => !!x))).sort().join(','),
    [itemIds],
  );
  const [tracked, setTracked] = useState<Set<string> | null>(null);

  useEffect(() => {
    let cancelled = false;
    const institutionId = AuthService.getSelectedInstitution();
    if (!canView || !institutionId || key === '') {
      setTracked(key === '' ? new Set() : null);
      return undefined;
    }
    SerialService.trackedItems(institutionId, key.split(','))
      .then((ids) => { if (!cancelled) setTracked(new Set(ids)); })
      .catch(() => { if (!cancelled) setTracked(null); });
    return () => { cancelled = true; };
  }, [key, canView]);

  return {
    /** True/false when known; undefined when unknown. */
    isTracked: (itemId?: string | null): boolean | undefined =>
      !itemId ? false : tracked === null ? undefined : tracked.has(itemId),
    anyTracked: tracked !== null && tracked.size > 0,
    known: tracked !== null,
  };
}
